import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { SFStack } from "../grid";
import SFTypography from "../typography";
import SFDialog from "../dialog";
import {
  SFHelpSupportIcon,
  SFLoadingIcon,
  SFCheckIcon,
  SFRevertIcon,
} from "../icons";
import getStyled from "../../hooks/getStyled";
import CreateReactPortal from "../../hooks/createReactPortal";
import SFDivider from "../divider";
import {
  getLongDateTimeWithDayPrecision,
  getTwelveHourTime,
} from "../../services/dateService";
import SFButton from "../button";
import SFTooltip from "../tooltip";
import { setAutoSaveBar } from "../../redux/reducers/pageLayoutReducer";
import { hasPermission } from "../authorized";
import SFAsyncActionButton from "../button/asyncActionButton";
import { SFPublishedWithChangesIcon } from "../icons";

const { styled } = getStyled();

const SFStackStyled = styled(SFStack)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  padding: `${theme.spacing(0)} ${theme.spacing(5)} ${theme.spacing(
    0
  )} ${theme.spacing(2.5)}`,
  bottom: 0,
  width: "100%",
  height: "39px",
  borderTop: `1px solid ${theme.palette.grey200.main}`,
}));

const LoadingIconWrapper = styled(SFStack)(({ theme }) => ({
  "& .MuiSvgIcon-root": {
    animation: "spin 3s linear infinite",
  },
}));

const HelpSupportIconWrapper = styled("div")(({ theme }) => ({
  display: "flex",
}));

const SFAutoSaveBar = ({
  draftMode = false,
  lastPublishedAt = null,
  lastPublishedLabel = null,
  savedAt = null,
  savedAtLabel = null,
  tooltipMessage = null,
  onRevert,
  saving = false,
  savingLabel = null,
  disableConfirmation = false,
  disableBarInfo = false,
  disableSavedAtInfo = false,
  confirmDialogProps = {
    message: null,
    title: null,
    confirmLabel: null,
    cancelLabel: null,
    variant: "info",
  },
  revertPermission = null,
  readOnly = false,
  publishing = false,
  onPublishClick,
  hidePublishButton = false,
  disablePublishButton = false,
  reverting = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [componentReady, setComponentReady] = useState(false);
  const reduxState = useSelector((state) => state.pageLayout.autoSaveBar);

  useEffect(() => {
    //initialize the auto save bar state
    dispatch(
      setAutoSaveBar({
        savedAt,
        lastPublishedAt,
        saving,
        publishing,
        reverting,
      })
    );
    setComponentReady(true);
  }, []);

  const clickRevertHandler = () => {
    if (disableConfirmation) {
      onRevert();
    } else {
      setDialogOpen(true);
    }
  };

  const confirmRevertHandler = () => {
    setDialogOpen(false);
    onRevert();
  };

  return CreateReactPortal(
    (reduxState.savedAt ||
      reduxState.saving ||
      reduxState.lastPublishedAt ||
      reduxState.publishing ||
      reduxState.reverting) &&
      componentReady && (
      <>
        <SFDialog
          variant={confirmDialogProps.variant ?? "info"}
          title={
            confirmDialogProps.title ??
              t("components.autoSaveBar.confirmationDialog.header")
          }
          message={confirmDialogProps.message}
          open={dialogOpen}
          closeButton
          onClose={() => setDialogOpen(false)}
          primaryButton
          primaryButtonProps={{
            label:
                confirmDialogProps.confirmLabel ??
                t("components.autoSaveBar.confirmationDialog.confirmBtn"),
            onClick: confirmRevertHandler,
          }}
          secondaryButton
          secondaryButtonProps={{
            label:
                confirmDialogProps.cancelLabel ??
                t("components.autoSaveBar.confirmationDialog.cancelBtn"),
            onClick: () => setDialogOpen(false),
          }}
        />
        <SFStackStyled
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <SFStack direction="row" alignItems="center" spacing={1.25}>
            {!disableBarInfo && (
              <SFStack direction="row" alignItems="center" spacing={0.625}>
                <SFTypography variant="body2" component="div">
                  <Trans
                    i18nKey={
                      draftMode
                        ? "components.autoSaveBar.draftModeLabel"
                        : "components.autoSaveBar.autoSaveModeLabel"
                    }
                  />
                </SFTypography>
                {tooltipMessage && (
                  <SFTooltip
                    placement="bottom"
                    width="wide"
                    content={tooltipMessage}
                  >
                    <HelpSupportIconWrapper>
                      <SFHelpSupportIcon />
                    </HelpSupportIconWrapper>
                  </SFTooltip>
                )}
              </SFStack>
            )}
            {reduxState.lastPublishedAt &&
                !reduxState.saving &&
                !reduxState.publishing &&
                !reduxState.reverting && (
              <>
                {!disableBarInfo && (
                  <SFDivider orientation="vertical" flexItem />
                )}
                <SFTypography variant="body2" component="div">
                  <Trans
                    i18nKey={"components.autoSaveBar.textDateLabel"}
                    values={{
                      text:
                            lastPublishedLabel ??
                            t("components.autoSaveBar.lastPublishedLabel"),
                      date: getLongDateTimeWithDayPrecision(
                        reduxState.lastPublishedAt
                      ),
                    }}
                  />
                </SFTypography>
                {draftMode && onRevert && reduxState.savedAt && (
                  <SFButton
                    variant="text-link"
                    icon={<SFRevertIcon />}
                    onClick={clickRevertHandler}
                    disabled={!hasPermission(revertPermission) || readOnly}
                  >
                    <Trans i18nKey={"components.autoSaveBar.revertLabel"} />
                  </SFButton>
                )}
              </>
            )}
          </SFStack>
          <SFStack direction="row" alignItems="center" spacing={0.625}>
            {reduxState.publishing ? (
              <>
                <LoadingIconWrapper>
                  <SFLoadingIcon color="grey550" />
                </LoadingIconWrapper>
                <SFTypography variant="buttonDefault" component="div">
                  <Trans
                    i18nKey={
                      draftMode
                        ? "components.autoSaveBar.publishingDraftLabel"
                        : "components.autoSaveBar.publishingLabel"
                    }
                  />
                </SFTypography>
              </>
            ) : reduxState.saving ? (
              <>
                <LoadingIconWrapper>
                  <SFLoadingIcon color="grey550" />
                </LoadingIconWrapper>
                <SFTypography variant="buttonDefault" component="div">
                  {savingLabel ?? (
                    <Trans
                      i18nKey={
                        draftMode
                          ? "components.autoSaveBar.savingDraftLabel"
                          : "components.autoSaveBar.savingLabel"
                      }
                    />
                  )}
                </SFTypography>
              </>
            ) : reduxState.reverting ? (
              <>
                <LoadingIconWrapper>
                  <SFLoadingIcon color="grey550" />
                </LoadingIconWrapper>
                <SFTypography variant="buttonDefault" component="div">
                  <Trans
                    i18nKey={
                      draftMode
                        ? "components.autoSaveBar.revertingDraftLabel"
                        : "components.autoSaveBar.revertingLabel"
                    }
                  />
                </SFTypography>
              </>
            ) : (
              reduxState.savedAt &&
                !disableSavedAtInfo && (
                <SFStack direction="row" alignItems="center" spacing={2.5}>
                  <SFStack direction="row" spacing={0.5}>
                    <SFCheckIcon size="small" color="green300" />
                    <SFTypography variant="body2" component="div">
                      <Trans
                        i18nKey={"components.autoSaveBar.textDateLabel"}
                        values={{
                          date: getTwelveHourTime(reduxState.savedAt),
                          text:
                              savedAtLabel ??
                              t(
                                draftMode
                                  ? "components.autoSaveBar.draftSavedLabel"
                                  : "components.autoSaveBar.savedLabel"
                              ),
                        }}
                      />
                    </SFTypography>
                  </SFStack>
                  {onPublishClick &&
                      typeof onPublishClick === "function" &&
                      !hidePublishButton && (
                    <SFAsyncActionButton
                      onClick={onPublishClick}
                      variant="primary"
                      disabled={disablePublishButton}
                      startIcon={<SFPublishedWithChangesIcon />}
                    >
                      {t("components.autoSaveBar.publishButtonLabel")}
                    </SFAsyncActionButton>
                  )}
                </SFStack>
              )
            )}
          </SFStack>
        </SFStackStyled>
      </>
    ),
    document.getElementById("autoSaveBarRoot")
  );
};

SFAutoSaveBar.propTypes = {
  /** Determined if the auto save bar is in draft mode else is using the auto save functionality.
   * If the draft mode is enabled a revert button is also rendered.
   */
  draftMode: PropTypes.bool,
  /** The date when the last published version was saved. */
  lastPublishedAt: PropTypes.string,
  /** The label displayed next to last published date */
  lastPublishedLabel: PropTypes.string,
  /** The date when the current version was saved. */
  savedAt: PropTypes.string,
  /** The label displaying next to savedAt date.
   *  Default to "Draft saved" or "Saved" based on draftMode.
   */
  savedAtLabel: PropTypes.string,
  /**The tooltip label for the info icon. */
  tooltipMessage: PropTypes.string,
  /** Action definition for revert button click. */
  onRevert: PropTypes.func,
  /** Determines if the auto save bar is in saving state.
   * In this case we are showing a loading gif and a saving label.
   */
  saving: PropTypes.bool,
  /** The label displaying next to saving gif.
   * Default to "Saving..." or "Saving draft..." based on draftMode.
   */
  savingLabel: PropTypes.string,
  /** Determines if the confirmation dialog should be disabled when clicking on the revert button. */
  disableConfirmation: PropTypes.bool,
  /** Determines if bar information message will be displayed (You are in draft/auto-save mode). */
  disableBarInfo: PropTypes.bool,
  /** Determines if the saved at date will be displayed. */
  disableSavedAtInfo: PropTypes.bool,
  /** The confirmation dialog properties. */
  confirmDialogProps: PropTypes.shape({
    message: PropTypes.string,
    title: PropTypes.string,
    confirmLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    variant: PropTypes.string,
  }),
  /** Disables button interactions. */
  readOnly: PropTypes.bool,
  /** Determines if the auto save bar is in publishing state.
   * In this case we are showing a loading gif and a publishing label.
   */
  publishing: PropTypes.bool,
};

export default SFAutoSaveBar;
